import { css } from '@emotion/react'

import sanitizeHtml from '../../../../../utils/sanitize'
import { SecondaryButton } from '../../../../shared/buttons'
import {
  primaryTextColor,
  secondaryGrayColor,
  primaryYellow
} from '../../../../../lib/styles/colors'
import { body5Css, body6Css } from '../../../../../lib/styles/typography'
import { findAndReplaceEscapedHyperlinks } from '../../../../../lib/utils'
import { srOnlyCss } from '../../../../../lib/styles/cssStyles'
import { customFocusIndicator } from '../../../../../lib/styles/focusIndicator'

const formatComment = (comment: string) => {
  const html = findAndReplaceEscapedHyperlinks(comment)
  return sanitizeHtml(html, {
    allowedTags: ['a', 'p']
  })
}

type CommentProps = {
  /** optional css class */
  className?: string
  /** Comment text */
  comment: string
  /** Date the comment was created */
  date: string
  /** ID of the comment */
  id: string
  /** Indicates if this plan is a template */
  isTemplate: boolean
  /** Name of the person who created the comment */
  name: string
  /** Event callback when a comment is deleted */
  onDelete: () => void
  /** Whether the comment has been read by the current user */
  read: boolean
  /** Whether or not to show the delete button,
   * should only be shown if the comment was added
   * by the currently logged in user.
   */
  showDelete: boolean
  /** Hide the new comment indicator after the comments have been viewed */
  showNewCommentIndicator: boolean
}

const dividerCss = css`
  border-top: 1px solid ${secondaryGrayColor};
  margin-top: 10px;
  padding-top: 15px;

  /* WCAG 2.4.7: Visible focus indicator */
  ${customFocusIndicator('#0078d4')}
`

const nameCss = css`
  ${body6Css};
  display: inline-block;
  padding-bottom: 3px;
`

const timestampCss = css`
  ${body5Css};
  padding-bottom: 12px;
`

const textCss = css`
  ${body5Css};
  color: ${primaryTextColor};
  p {
    margin-block-start: 0;
    margin-block-end: 0;
  }
  /* show empty line breaks generated by RichTextField */
  p:empty:before {
    content: ' ';
    white-space: pre;
  }
`

const deleteButtonCss = css`
  margin-left: -15px;
  padding-top: 10px;
`

const unreadCss = css`
  background-color: ${primaryYellow};
  border-radius: 50%;
  display: inline-block;
  margin-right: 12px;
  height: 10px;
  width: 10px;
`

const Comment = ({
  className = '',
  comment,
  date,
  id,
  isTemplate,
  name,
  onDelete,
  read,
  showDelete,
  showNewCommentIndicator
}: CommentProps) => {
  const containerCss = css`
    padding: ${showDelete ? '0 20px' : '0 20px 10px 20px'};
  `

  return (
    // Comment container should be a non-interactive region, only Delete button should be interactive
    <div className={className} css={dividerCss} id={id}>
      <div css={containerCss}>
        {!read && !isTemplate && showNewCommentIndicator && (
          <span>
            <span css={unreadCss} aria-hidden="true" />
            <span css={srOnlyCss}>Unread comment.</span>
          </span>
        )}
        <div css={nameCss}>
          <span css={srOnlyCss}>Comment author</span>
          {name}
        </div>
        <div css={timestampCss}>
          <span css={srOnlyCss}>Comment date</span>
          {date}
        </div>
        <div css={textCss}>
          <span css={srOnlyCss}>Comment text</span>
          <span
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: formatComment(comment)
            }}
          />
        </div>
        {showDelete && (
          <div css={deleteButtonCss}>
            <SecondaryButton
              ariaLabel={`Delete comment by ${name} from ${date}`}
              onClick={onDelete}
            >
              Delete
            </SecondaryButton>
          </div>
        )}
      </div>
    </div>
  )
}

export default Comment
